import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Plus, Ticket, CheckCircle2, HandHelping, X, Users as UsersIcon, Undo2,
  Trash2, Calendar, Search, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  OPEN:        { label: "Open",        bg: "bg-zinc-500",  row: "bg-white" },
  IN_PROGRESS: { label: "In Progress", bg: "bg-[#FFB800]", row: "bg-[#FFF6D6]" },
  DONE:        { label: "Done",        bg: "bg-[#22A06B]", row: "bg-[#DFF3E8]" },
};

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

// Build a list of the last N calendar days (IST) ending today, most-recent first.
function lastNDays(n) {
  const out = [];
  const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}
function shortDayLabel(iso) {
  const [y, m, d] = iso.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  const t = todayIST();
  if (iso === t) return "Today";
  const yesterday = new Date(new Date(t).getTime() - 24 * 3600 * 1000).toISOString().slice(0, 10);
  if (iso === yesterday) return "Yesterday";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" });
}

function computeStatus(t) {
  if (t.punched_out_at) return "DONE";
  if ((t.helpers || []).length > 0) return "IN_PROGRESS";
  return "OPEN";
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [scope, setScope] = useState("ALL"); // ALL | MINE
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [dayTab, setDayTab] = useState(todayIST()); // YYYY-MM-DD selected day tab
  const isToday = dayTab === todayIST();

  async function load() {
    try {
      const [a, b] = await Promise.all([
        api.get("/tasks", { params: scope === "MINE" ? { mine: true } : {} }),
        api.get("/tasks/employees"),
      ]);
      setTasks(a.data);
      setEmployees(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, [scope]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) =>
      (t.title || "").toLowerCase().includes(q) ||
      (t.ticket_no || "").toLowerCase().includes(q) ||
      (t.assigned_to_name || "").toLowerCase().includes(q) ||
      (t.notes || "").toLowerCase().includes(q)
    );
  }, [tasks, search]);

  const open = filtered.filter((t) => !t.punched_out_at);
  const done = filtered.filter((t) => t.punched_out_at);

  const counts = {
    OPEN: filtered.filter((t) => computeStatus(t) === "OPEN").length,
    IN_PROGRESS: filtered.filter((t) => computeStatus(t) === "IN_PROGRESS").length,
    DONE: done.length,
  };

  async function help(t) {
    try { await api.post(`/tasks/${t.id}/help`); toast.success("Joined as helper"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function unhelp(t) {
    try { await api.post(`/tasks/${t.id}/unhelp`); toast.success("Left the helper list"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function punchOut(t, notes) {
    try {
      await api.post(`/tasks/${t.id}/punch-out`, { notes: notes || "" });
      toast.success("Punched out");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function deletePunch(t, entryId) {
    if (!window.confirm("Remove this punch-out entry?")) return;
    try { await api.delete(`/tasks/${t.id}/punch-out/${entryId}`); toast.success("Entry removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function reopen(t) {
    try { await api.post(`/tasks/${t.id}/reopen`); toast.success("Reopened"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function del(t) {
    if (!window.confirm(`Delete ticket ${t.ticket_no}?`)) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="tasks-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2 inline-flex items-center gap-2">
            <Ticket size={12} /> / SHARED · TASK LEDGER
          </div>
          <h1 className="section-title text-5xl lg:text-6xl">Task Ledger.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">
            One assignee per ticket owns punch-out. Anyone else can lend a hand.
            {user?.name && <> Signed in as <span className="font-bold">{user.name}</span>.</>}
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="brut-btn px-5 py-3 inline-flex items-center gap-2 self-start" data-testid="new-task-btn">
          <Plus size={16} /> New Ticket
        </button>
      </header>

      {/* Scope + counts */}
      <div className="flex flex-wrap gap-2 mb-4">
        <ScopeBtn id="ALL" active={scope} setScope={setScope} label="All" count={filtered.length} />
        <ScopeBtn id="MINE" active={scope} setScope={setScope} label="Mine" count={filtered.length} />
        <div className="w-2" />
        <span className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider bg-white`}>Open ({counts.OPEN})</span>
        <span className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider bg-[#FFF6D6]`}>In Progress ({counts.IN_PROGRESS})</span>
        <span className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider bg-[#DFF3E8]`}>Done ({counts.DONE})</span>
      </div>

      {/* Day tabs — pick a day to browse punches. "Today" shows the live task ledger. */}
      <div className="brut-card p-3 mb-5" data-testid="tasks-day-tabs">
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays size={13} className="text-zinc-500" />
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">
            Daily Punch Book — {isToday ? "live view" : "read-only for past day"}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {lastNDays(8).map((iso) => {
            const active = iso === dayTab;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setDayTab(iso)}
                data-testid={`day-tab-${iso}`}
                className={`px-3 py-1.5 border-2 border-[#2F343A] font-mono-ds text-[11px] font-bold uppercase transition-all ${
                  active
                    ? "bg-[#7A3E1F] text-white shadow-[3px_3px_0_0_#2F343A]"
                    : "bg-white hover:bg-zinc-100"
                }`}
              >
                {shortDayLabel(iso)}
              </button>
            );
          })}
          <span className="mx-2 text-zinc-400 font-mono-ds text-xs">·</span>
          <input
            type="date"
            value={dayTab}
            max={todayIST()}
            onChange={(e) => setDayTab(e.target.value || todayIST())}
            className="brut-input font-mono-ds max-w-[170px] text-xs py-1.5"
            data-testid="day-tab-picker"
          />
          {!isToday && (
            <button
              type="button"
              onClick={() => setDayTab(todayIST())}
              className="ml-auto brut-btn-outline px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
              data-testid="day-tab-back-to-today"
            >
              <Calendar size={12} /> Back to Today
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket #, title, assignee, notes…" className="brut-input pl-10" data-testid="task-search" />
      </div>

      {/* Historical day view (read-only) OR live task ledger for today */}
      {!isToday ? (
        <HistoricalDayView date={dayTab} isAdmin={user?.role === "admin"} />
      ) : (
        <>
          {/* Open + In Progress */}
          <section className="mb-8">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Active ({open.length})</div>
            {open.length === 0 ? (
              <div className="brut-card p-8 text-center text-zinc-500 font-mono-ds text-xs uppercase" data-testid="tasks-empty-active">
                No active tickets · Tap &quot;New Ticket&quot; to add one
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {open.map((t) => (
                  <TaskCard key={t.id} task={t} user={user} onHelp={help} onUnhelp={unhelp} onPunchOut={punchOut} onDeletePunch={deletePunch} onDelete={del} />
                ))}
              </div>
            )}
          </section>

          {done.length > 0 && (
            <section>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Completed ({done.length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {done.map((t) => (
                  <TaskCard key={t.id} task={t} user={user} onHelp={help} onUnhelp={unhelp} onPunchOut={punchOut} onDeletePunch={deletePunch} onReopen={reopen} onDelete={del} />
                ))}
              </div>
            </section>
          )}

          {user?.role === "admin" && <DailyPunchLog />}
        </>
      )}

      {showForm && <NewTaskModal employees={employees} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ScopeBtn({ id, active, setScope, label, count }) {
  const isActive = active === id;
  return (
    <button onClick={() => setScope(id)} data-testid={`scope-${id.toLowerCase()}`}
      className={`px-4 py-2 border-2 border-black font-display font-black text-xs uppercase tracking-wider transition-all ${
        isActive ? "bg-black text-white shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
      }`}>
      {label} {isActive && <span className="ml-1 font-mono-ds opacity-70">({count})</span>}
    </button>
  );
}

function HistoricalDayView({ date, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get("/tasks/punch-log", { params: { date } });
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const byEmp = useMemo(() => {
    const map = {};
    rows.forEach((r) => { (map[r.user_name] = map[r.user_name] || []).push(r); });
    return map;
  }, [rows]);

  const label = shortDayLabel(date);
  return (
    <section className="brut-card p-5" data-testid="historical-day-view">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">READ-ONLY · {isAdmin ? "EVERYONE" : "YOUR PUNCHES"}</div>
          <div className="font-display font-black text-2xl">{label} · {date}</div>
          <div className="font-mono-ds text-[10px] uppercase text-zinc-500 mt-1">{rows.length} punch-out{rows.length === 1 ? "" : "s"} logged</div>
        </div>
        <button onClick={load} className="brut-btn-outline px-3 py-2 text-xs" data-testid="historical-refresh">Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="brut-card p-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Punch-outs</div>
          <div className="font-display font-black text-2xl">{rows.length}</div>
        </div>
        <div className="brut-card p-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{isAdmin ? "Employees" : "Tickets"}</div>
          <div className="font-display font-black text-2xl">{isAdmin ? Object.keys(byEmp).length : new Set(rows.map((r) => r.ticket_no)).size}</div>
        </div>
        <div className="brut-card p-3 md:col-span-2">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Contributors</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.entries(byEmp).map(([name, list]) => (
              <span key={name} className="px-2 py-1 border-2 border-[#2F343A] font-mono-ds text-[10px] font-bold uppercase bg-white">
                {name} <span className="opacity-60">· {list.length}</span>
              </span>
            ))}
            {Object.keys(byEmp).length === 0 && <span className="font-mono-ds text-[10px] uppercase text-zinc-400">no entries yet</span>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th className="w-20">Time</th>
              <th>Employee</th>
              <th>Ticket</th>
              <th>Task</th>
              <th>What they did</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.entry_id} data-testid={`historical-row-${r.entry_id}`}>
                <td className="font-mono-ds font-bold whitespace-nowrap">{r.time}</td>
                <td className="font-display font-bold">{r.user_name}</td>
                <td className="font-mono-ds text-xs">{r.ticket_no}</td>
                <td className="font-display font-bold max-w-[240px] truncate" title={r.title}>{r.title}</td>
                <td className="text-xs whitespace-pre-wrap">{r.notes || <span className="text-zinc-400">—</span>}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-zinc-500 font-mono-ds text-xs uppercase">No punch-outs on {date}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DailyPunchLog() {
  const [date, setDate] = useState(() => {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    return d.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = date ? { date } : {};
      const res = await api.get("/tasks/punch-log", { params });
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [date]);

  const byEmp = useMemo(() => {
    const map = {};
    rows.forEach((r) => { (map[r.user_name] = map[r.user_name] || []).push(r); });
    return map;
  }, [rows]);

  return (
    <section className="mt-10 brut-card p-5" data-testid="daily-punch-log">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">ADMIN ONLY</div>
          <div className="font-display font-black text-xl">Daily Punch Log</div>
          <div className="font-mono-ds text-[10px] uppercase text-zinc-500 mt-1">Everyone&apos;s work for the day, in one place</div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="brut-input font-mono-ds max-w-[180px]" data-testid="punch-log-date" />
          <button onClick={load} className="brut-btn-outline px-3 py-2 text-xs" data-testid="punch-log-refresh">Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="brut-card p-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Punch-outs</div>
          <div className="font-display font-black text-2xl">{rows.length}</div>
        </div>
        <div className="brut-card p-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Employees</div>
          <div className="font-display font-black text-2xl">{Object.keys(byEmp).length}</div>
        </div>
        <div className="brut-card p-3 md:col-span-2">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">By employee</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.entries(byEmp).map(([name, list]) => (
              <span key={name} className="px-2 py-1 border-2 border-[#2F343A] font-mono-ds text-[10px] font-bold uppercase bg-white">
                {name} <span className="opacity-60">· {list.length}</span>
              </span>
            ))}
            {Object.keys(byEmp).length === 0 && <span className="font-mono-ds text-[10px] uppercase text-zinc-400">no entries yet</span>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th className="w-20">Time</th>
              <th>Employee</th>
              <th>Ticket</th>
              <th>Task</th>
              <th>What they did</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.entry_id} data-testid={`punch-log-row-${r.entry_id}`}>
                <td className="font-mono-ds font-bold whitespace-nowrap">{r.time}</td>
                <td className="font-display font-bold">{r.user_name}</td>
                <td className="font-mono-ds text-xs">{r.ticket_no}</td>
                <td className="font-display font-bold max-w-[240px] truncate" title={r.title}>{r.title}</td>
                <td className="text-xs whitespace-pre-wrap">{r.notes || <span className="text-zinc-400">—</span>}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-zinc-500 font-mono-ds text-xs uppercase">No punch-outs on {date}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TaskCard({ task, user, onHelp, onUnhelp, onPunchOut, onDeletePunch, onReopen, onDelete }) {
  const [showPunchModal, setShowPunchModal] = useState(false);
  const status = computeStatus(task);
  const meta = STATUS_META[status];
  const isAssignee = user?.id === task.assigned_to_id;
  const isHelper = (task.helpers || []).some((h) => h.id === user?.id);
  const isCreator = user?.id === task.created_by_id || user?.role === "admin";
  const done = !!task.punched_out_at;
  const punchOuts = task.punch_outs || [];
  const myPunches = punchOuts.filter((p) => p.user_id === user?.id).length;

  return (
    <div className={`brut-card p-5 ${meta.row}`} data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{task.ticket_no}</div>
          <div className="font-display font-black text-xl leading-tight break-words">{task.title}</div>
        </div>
        <span className={`brut-badge ${meta.bg} text-white shrink-0`} style={{ borderColor: "#2F343A" }} data-testid={`task-status-${task.id}`}>
          {meta.label}
        </span>
      </div>

      {task.notes && <div className="text-sm text-zinc-700 mb-3 whitespace-pre-wrap">{task.notes}</div>}

      <div className="space-y-1.5 mb-3">
        <div className="font-mono-ds text-[11px] uppercase tracking-wider">
          <span className="opacity-60">Assigned →</span>{" "}
          <span className="font-bold">{task.assigned_to_name}</span>
          {isAssignee && <span className="ml-2 brut-badge bg-black text-white text-[9px] px-1.5 py-0.5" style={{ borderColor: "#2F343A" }}>YOU</span>}
        </div>
        <div className="font-mono-ds text-[10px] uppercase text-zinc-500">
          Shared work — anyone can punch out with their own note
        </div>
        {task.due_date && (
          <div className="font-mono-ds text-[10px] uppercase text-zinc-500 inline-flex items-center gap-1">
            <Calendar size={11} /> Due {task.due_date}
          </div>
        )}
      </div>

      {(task.helpers || []).length > 0 && (
        <div className="mb-3">
          <div className="font-mono-ds text-[10px] uppercase tracking-widest text-zinc-500 mb-1 inline-flex items-center gap-1"><UsersIcon size={11} /> Helpers</div>
          <div className="flex flex-wrap gap-1.5">
            {(task.helpers || []).map((h) => (
              <span key={h.id} className={`px-2 py-1 border-2 border-black font-mono-ds text-[10px] font-bold uppercase ${h.id === user?.id ? "bg-black text-white" : "bg-white"}`}>{h.name}{h.id === user?.id && " · you"}</span>
            ))}
          </div>
        </div>
      )}

      {punchOuts.length > 0 && (
        <div className="mb-3 border-2 border-[#22A06B] bg-white">
          <div className="bg-[#22A06B] text-white px-3 py-1.5 font-mono-ds text-[10px] uppercase tracking-widest inline-flex items-center gap-2 w-full">
            <CheckCircle2 size={12} /> Punch-out log ({punchOuts.length})
          </div>
          <div className="divide-y-2 divide-dashed divide-zinc-200">
            {punchOuts.map((p) => (
              <div key={p.id} className="p-2 flex items-start justify-between gap-2" data-testid={`punch-entry-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-ds text-[11px] font-bold">{p.user_name}</div>
                  <div className="font-mono-ds text-[10px] text-zinc-500">{p.date} · {p.time}</div>
                  {p.notes && <div className="text-xs text-zinc-700 mt-1 whitespace-pre-wrap">{p.notes}</div>}
                </div>
                {(p.user_id === user?.id || user?.role === "admin") && (
                  <button onClick={() => onDeletePunch(task, p.id)} className="p-1 border-2 border-black hover:bg-[#FF3333] hover:text-white shrink-0" data-testid={`delete-punch-${p.id}`}>
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        {!isAssignee && !isHelper && !done && (
          <button onClick={() => onHelp(task)} className="brut-btn-outline px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid={`lend-a-hand-${task.id}`}>
            <HandHelping size={13} /> Lend a hand
          </button>
        )}
        {isHelper && (
          <button onClick={() => onUnhelp(task)} className="brut-btn-outline px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid={`leave-helper-${task.id}`}>
            <X size={13} /> Leave helper list
          </button>
        )}
        <button onClick={() => setShowPunchModal(true)} className="brut-btn px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid={`punch-out-${task.id}`}>
          <CheckCircle2 size={13} /> Punch Out {myPunches > 0 && <span className="opacity-70">(again)</span>}
        </button>
        {done && (isAssignee || user?.role === "admin") && onReopen && (
          <button onClick={() => onReopen(task)} className="brut-btn-outline px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid={`reopen-${task.id}`}>
            <Undo2 size={13} /> Reset &quot;Done&quot; pill
          </button>
        )}
        {isCreator && (
          <button onClick={() => onDelete(task)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white ml-auto" title="Delete" data-testid={`delete-task-${task.id}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="mt-3 pt-2 border-t-2 border-dashed border-zinc-300 font-mono-ds text-[9px] uppercase tracking-widest text-zinc-500">
        Opened by {task.created_by_name} · {fmtDateTime(task.created_at)}
      </div>

      {showPunchModal && (
        <PunchOutModal
          task={task}
          onClose={() => setShowPunchModal(false)}
          onSubmit={(notes) => { onPunchOut(task, notes); setShowPunchModal(false); }}
        />
      )}
    </div>
  );
}

function PunchOutModal({ task, onClose, onSubmit }) {
  const [notes, setNotes] = useState("");
  const ist = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="punch-out-modal">
      <div className="brut-card w-full max-w-md bg-white">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#2F343A] bg-[#22A06B] text-white">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">PUNCH OUT · {task.ticket_no}</div>
            <h2 className="font-display font-black text-lg leading-tight">{task.title}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="brut-card-flat p-3 bg-[#FDF6EE]">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Stamping now (IST)</div>
            <div className="font-mono-ds font-black text-base">{ist}</div>
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">What did you get done? (optional)</div>
            <textarea rows={4} className="brut-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Printed 10 pcs · packed 6 orders · pending 4 pcs due to size issue" data-testid="punch-notes-input" autoFocus />
          </div>
        </div>
        <div className="border-t-2 border-[#2F343A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2">Cancel</button>
          <button onClick={() => onSubmit(notes)} className="brut-btn px-5 py-2" data-testid="submit-punch-btn">
            <CheckCircle2 size={14} className="inline mr-1" /> Punch Out
          </button>
        </div>
      </div>
    </div>
  );
}

function NewTaskModal({ employees, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [assignedTo, setAssignedTo] = useState(employees[0]?.id || "");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!assignedTo) { toast.error("Pick an assignee"); return; }
    setBusy(true);
    try {
      await api.post("/tasks", { title, assigned_to_id: assignedTo, notes, due_date: dueDate || null });
      toast.success("Ticket created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="new-task-modal">
      <div className="brut-card w-full max-w-lg bg-white">
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-black text-white">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">NEW TICKET</div>
            <h2 className="font-display font-black text-2xl">Assign work</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20" data-testid="close-new-task"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Title *</div>
            <input className="brut-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="e.g. Print DTF for Ayrton Senna" data-testid="new-task-title" />
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Assign to (single) *</div>
            <select className="brut-input" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} data-testid="new-task-assignee">
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.role}</option>)}
            </select>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mt-1">
              Only this person will be able to punch it out. Others can lend a hand.
            </div>
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Due date (optional)</div>
            <input type="date" className="brut-input font-mono-ds max-w-[200px]" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="new-task-due" />
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Notes (optional)</div>
            <textarea rows={3} className="brut-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context / specs / links" data-testid="new-task-notes" />
          </div>
        </div>
        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-new-task">
            {busy ? "Creating…" : "Create Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}
